/**
 * Whether anyone has signed in on this device before (#1066).
 *
 * The homepage hero offers "Log in" instead of "Start your video journal" on a
 * device that has: visitors whose session had lapsed were tapping the big
 * button, landing on the sign-up form and hunting for the way in. The mark is
 * one constant, nothing about who signed in, so it outlives a sign-out by
 * design (a product call on the card). The hero keeps a "New here? Create an
 * account" link under the button for the next person on a shared device.
 *
 * Set from the auth provider's resolved state rather than from a sign-in form,
 * so every way into a session marks the device: a password, an emailed link,
 * SSO, or a session persisted from before this shipped. A sign-up abandoned
 * before the provider signed anyone in leaves the device new.
 *
 * A visitor whose session is about to resume is not offered Log in. While the
 * provider is still resolving, the session hint (`reflect_signed_in`, see
 * `appGate`) says a session was live, and the hero keeps its usual label until
 * it says "Go to my journal". {@link offersLogIn} makes that call.
 *
 * Kept in `localStorage`, and every access is guarded. Where storage is
 * blocked the device reads as new, and the homepage falls back to the
 * new-visitor page every first visit already sees.
 *
 * The marketing pages are static files behind a CDN, so the server cannot
 * choose the label. {@link SIGNED_IN_BEFORE_SCRIPT} reads the mark while the
 * HTML is still parsing, before the hero paints. {@link useSignedInBefore}
 * takes over from it once React hydrates.
 */
import { useSyncExternalStore } from "react";

import { webStorage } from "../browserStorage";
import { hasSessionHint, SESSION_HINT_COOKIE } from "./appGate";
import type { AuthSnapshot } from "./token-provider";

/** The storage key. Its one value is {@link MARK}. */
export const SIGNED_IN_BEFORE_STORAGE_KEY = "reflect.auth.signedInBefore";

/** The stored value. A constant, so the mark says nothing about the account. */
const MARK = "1";

/**
 * The attribute the homepage root carries on a device that has signed in
 * before. The stylesheet swaps the hero's label on it, and spells it out:
 * renaming it here means renaming it in `figmaVariantA.css` too.
 */
export const SIGNED_IN_BEFORE_ATTRIBUTE = "data-signed-in-before";

/** Mark this device as one somebody has signed in on. */
export function rememberSignedIn(): void {
  try {
    webStorage("local")?.setItem(SIGNED_IN_BEFORE_STORAGE_KEY, MARK);
  } catch {
    // A full or blocked storage: the device keeps reading as new.
  }
}

/**
 * Whether this device carries the mark. A pure read, so a render may call it.
 *
 * @returns `true` when somebody has signed in here before.
 */
export function hasSignedInBefore(): boolean {
  try {
    return webStorage("local")?.getItem(SIGNED_IN_BEFORE_STORAGE_KEY) === MARK;
  } catch {
    return false;
  }
}

/** Re-read on another tab's write: a sign-in there marks this device too. */
function subscribe(onChange: () => void): () => void {
  window.addEventListener("storage", onChange);
  return () => window.removeEventListener("storage", onChange);
}

/** The server render and hydration read the device as new. */
function serverSnapshot(): boolean {
  return false;
}

/**
 * {@link hasSignedInBefore} as a hook. Hydration reads `false`, matching the
 * static HTML, and the page re-renders with the device's answer straight
 * after. The pre-paint script has already shown that answer by then.
 *
 * @returns `true` when somebody has signed in here before.
 */
export function useSignedInBefore(): boolean {
  return useSyncExternalStore(subscribe, hasSignedInBefore, serverSnapshot);
}

/**
 * Whether the hero offers Log in. Only on a marked device with nobody signed
 * in, and not while the provider is still resolving a session the hint says
 * was live: that visitor would see Log in, then "Go to my journal", with the
 * page shifting under them as the "New here?" line went.
 *
 * @param signedInBefore - {@link useSignedInBefore}'s answer.
 * @param status - the auth snapshot's status.
 * @returns `true` when the hero should say Log in.
 */
export function offersLogIn(signedInBefore: boolean, status: AuthSnapshot["status"]): boolean {
  if (!signedInBefore || status === "authenticated") return false;
  return status !== "loading" || !hasSessionHint();
}

/**
 * An inline script that sets {@link SIGNED_IN_BEFORE_ATTRIBUTE} on its parent
 * element when the hero should offer Log in. It runs before the provider has
 * resolved anything, so it is {@link offersLogIn} while loading: the mark, and
 * no session hint.
 *
 * Rendered as a raw `<script>` rather than through `next/script`, whose
 * `beforeInteractive` scripts are queued in the HTML and run only when the
 * runtime boots. This one has to run during parsing, before the hero below it
 * is painted. React does not run a `<script>` it creates on the client, so a
 * client-side visit relies on {@link useSignedInBefore} alone. That is enough
 * there, because nothing is painted before React renders.
 */
export const SIGNED_IN_BEFORE_SCRIPT =
  `try{if(localStorage.getItem(${JSON.stringify(SIGNED_IN_BEFORE_STORAGE_KEY)})===` +
  `${JSON.stringify(MARK)}&&("; "+document.cookie).indexOf(` +
  `${JSON.stringify(`; ${SESSION_HINT_COOKIE}=`)})<0)` +
  `document.currentScript.parentElement.setAttribute(` +
  `${JSON.stringify(SIGNED_IN_BEFORE_ATTRIBUTE)},"")}catch(e){}`;
